import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import Sheet from "../../../../../../components/shared/Sheet";
import Form from "./Form";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { useElementHeight } from "../../../../../../utils/hooks/useElementHeight";
import { useCompanyTimeFormat } from "../../../../../../utils/hooks/company";
import { getOrderedWeekdays } from "../../../helpers/getOrderedWeekdays";
import { processUnavailableBlocks, type UnavailableBlock } from "../../../helpers/data/calculateUnavailableEvents";
import { cn } from "../../../../../../lib/utils";
import type { Resource } from "../../../types/Resource";
import type { SelectedCellOrEvent } from "../../../types/ScheduleState";
import type { Event } from "../../../types/Event";

interface EventDrawerProps {
    openDeleteModal: ({ type, event, onSuccess }: { type: string, event: Event | SelectedCellOrEvent, onSuccess?: () => void }) => void;
}

function EventDrawer({ openDeleteModal }: EventDrawerProps) {
    const { t } = useTranslation();
    const [{ mode }] = useFilters();
    const { height: intervalsHeight, ref: intervalsRef } = useElementHeight();
    const timeFormat = useCompanyTimeFormat();
    const { dispatch, selectedCellOrEvent, allResources } = useScheduleContext();
    const orderedWeekdays = getOrderedWeekdays();

    const title: string = useMemo(() => {
        if (selectedCellOrEvent?.isTemplate) {
            return selectedCellOrEvent?.isCreating
                ? t(orderedWeekdays[moment(selectedCellOrEvent?.date).weekday()])
                : t(selectedCellOrEvent?.date);
        } else if (selectedCellOrEvent?.isUnassignedShift && selectedCellOrEvent?.isCreating) {
            return t("create-unassigned-shift");
        } else if (selectedCellOrEvent?.isUnassignedShift && !selectedCellOrEvent?.isCreating) {
            return t("edit-unassigend-shift");
        } else {
            return moment(selectedCellOrEvent?.date).format("dddd, MMM DD, YYYY");
        }
    }, [orderedWeekdays, selectedCellOrEvent, t]);

    const close = useCallback(() => {
        const id = selectedCellOrEvent?.id || undefined;
        if (id && id.startsWith("event_")) {
            dispatch({ type: "delete-event", payload: { id } });
        }
        if (id && id.startsWith("unassigned_shifts")) {
            dispatch({ type: "delete-unassigned-event", payload: { id } });
        }
        dispatch({ type: "selected-cell-or-event" });
    }, [dispatch, selectedCellOrEvent?.id]);

    const eventUser: Resource | undefined = useMemo(() => {
        const user = selectedCellOrEvent?.user;
        const id = typeof user === "object" ? (user as Resource)?.id : user;
        const resourceId = selectedCellOrEvent?.resourceId || id;

        return allResources?.find((item: Resource) => item?.id === resourceId)
            || (typeof user === "object" ? (user as Resource) : undefined);
    }, [selectedCellOrEvent?.user, selectedCellOrEvent?.resourceId, allResources]);

    const intervals: UnavailableBlock[] = useMemo(() => {
        const values = processUnavailableBlocks(eventUser?.intervals || []);
        return mode !== "month" ? values?.filter((item) =>
            moment(item.startDate).isSame(
                moment(selectedCellOrEvent?.startDate || selectedCellOrEvent?.date),
                "date"
            )) : [];
    }, [eventUser, selectedCellOrEvent?.startDate, selectedCellOrEvent?.date, mode]);

    const intervalWidth = useMemo(() => {
        if (intervals.length >= 2) return "w-1/3";
        return "w-full";
    }, [intervals?.length]);

    return (
        <Sheet
            open={!!selectedCellOrEvent}
            close={close}
            title={title}
            bodyClassName="tw-flex tw-flex-col tw-min-h-0 tw-overflow-hidden"
        >
            {intervals?.length > 0 && (
                <div
                    ref={intervalsRef}
                    className="tw-flex tw-flex-wrap tw-gap-1 tw-mb-4 tw-shrink-0 tw-items-center tw-justify-center tw-mr-4 max-h-65 tw-overflow-auto"
                >
                    {intervals.map((interval, index) => (
                        <div
                            key={index}
                            className={cn(
                                intervalWidth,
                                "tw-relative tw-flex tw-flex-col tw-text-red-500 tw-border tw-border-red-100 tw-bg-rose-100/35 tw-items-center tw-rounded-lg tw-px-7 tw-py-2 tw-text-sm"
                            )}
                        >
                            <h4 className="tw-font-semibold tw-text-red-500">
                                {t("unavailable")}
                            </h4>
                            {interval.allDay ? (
                                <span className="tw-mx-2 tw-text-xs">
                                    ({t("all-day")})
                                </span>
                            ) : (
                                <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs">
                                    <span>{moment(interval.startDate).format(timeFormat)}</span>
                                    <span>-</span>
                                    <span>{moment(interval.endDate).format(timeFormat)}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <Form
                close={close}
                openDeleteModal={openDeleteModal}
                intervals={intervals}
                intervalsHeight={intervalsHeight} />
        </Sheet>
    );
}

export default EventDrawer;
